/**
 * Structural views of the browser objects the engine touches. Real DOM objects satisfy them
 * (checked in browser.ts); tests supply fakes. Nothing here reads a global at import time.
 */
import type { Disposer } from './result.js';

export interface MediaStreamTrackLike {
  readonly id: string;
  readonly kind: string;
  readonly label: string;
  enabled: boolean;
  readonly readyState: 'live' | 'ended';
  stop(): void;
  getSettings(): { deviceId?: string };
  addEventListener(type: 'ended', listener: () => void): void;
  removeEventListener(type: 'ended', listener: () => void): void;
}

export interface MediaStreamLike {
  readonly id: string;
  getTracks(): MediaStreamTrackLike[];
  getAudioTracks(): MediaStreamTrackLike[];
}

export interface MediaDeviceInfoLike {
  readonly deviceId: string;
  readonly groupId: string;
  readonly kind: string;
  readonly label: string;
}

export type PermissionStateLike = 'granted' | 'denied' | 'prompt';

export interface PermissionWatch {
  readonly state: PermissionStateLike;
  onChange(callback: (state: PermissionStateLike) => void): Disposer;
}

/** Audio-only capture: no video, mono, browser voice processing on by default. */
export interface AudioCaptureConstraints {
  readonly audio: {
    readonly deviceId?: { readonly exact: string };
    readonly echoCancellation: boolean;
    readonly noiseSuppression: boolean;
    readonly autoGainControl: boolean;
    readonly channelCount: { readonly ideal: 1 };
  };
  readonly video: false;
}

/** Remote audio output. Browser implementation wraps an HTMLAudioElement that needs no DOM attachment. */
export interface AudioSink {
  setStream(stream: MediaStreamLike | null): void;
  /** '' selects the system default output. */
  setOutput(deviceId: string): Promise<void>;
  play(): Promise<void>;
  dispose(): void;
}

export interface MediaPlatform {
  readonly secureContext: boolean;
  readonly webrtcSupported: boolean;
  readonly outputSelectionSupported: boolean;
  getUserMedia(constraints: AudioCaptureConstraints): Promise<MediaStreamLike>;
  /** Independent call tracks from a session's muted microphone source. */
  cloneStream?(stream: MediaStreamLike): MediaStreamLike;
  enumerateDevices(): Promise<MediaDeviceInfoLike[]>;
  onDeviceChange(callback: () => void): Disposer;
  /** null when the Permissions API cannot report microphone state. */
  watchMicrophonePermission(): Promise<PermissionWatch | null>;
  createAudioSink(): AudioSink;
}

export interface LifecyclePlatform {
  isOnline(): boolean;
  onOnline(callback: () => void): Disposer;
  onOffline(callback: () => void): Disposer;
  onVisible(callback: () => void): Disposer;
}

/** One phone per browser profile (UX 7.1). Browser implementation uses the Web Locks API. */
export interface DeviceLock {
  acquire(options: { readonly steal: boolean }): Promise<DeviceLockHandle | null>;
}

export interface DeviceLockHandle {
  /** Resolves if another tab steals the lock. */
  readonly lost: Promise<void>;
  release(): void;
}

export const noLifecycle: LifecyclePlatform = {
  isOnline: () => true,
  onOnline: () => () => {},
  onOffline: () => () => {},
  onVisible: () => () => {},
};
